/**
 * Footer — semantic, accessible, responsive.
 * Structure: Brand block, Quick Links, Connect With Us (social), Legal.
 * Uses semantic HTML, table-free layout, responsive grid; keyboard and mobile friendly.
 */

import { FooterSection } from './FooterSection';
import { FooterNav } from './FooterNav';
import { SocialLinks } from './SocialLinks';
import { LegalLinks } from './LegalLinks';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden"
      role="contentinfo"
    >
      {/* Decorative background (presentational) */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden>
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Main content: responsive grid, all columns left-aligned on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 mb-10 text-left">
          {/* Brand block */}
          <section aria-labelledby="footer-brand-heading" className="lg:col-span-1 text-left">
            <h2 id="footer-brand-heading" className="text-xl font-bold mb-3 text-white text-left">
              Wincrest Logistics
            </h2>
            <p className="text-sm text-gray-300 leading-relaxed" style={{ lineHeight: 1.6 }}>
              Your trusted partner for reliable vehicle transportation services across the nation.
            </p>
          </section>

          {/* Quick Links */}
          <FooterSection title="Quick Links" id="footer-quick-links">
            <FooterNav />
          </FooterSection>

          {/* Connect With Us — inline icons + labels */}
          <FooterSection title="Connect With Us" id="footer-connect">
            <SocialLinks />
          </FooterSection>

          {/* Legal */}
          <FooterSection title="Legal" id="footer-legal">
            <LegalLinks />
          </FooterSection>
        </div>

        <div className="border-t border-gray-700/50 my-8" aria-hidden />

        <div className="text-center">
          <p className="text-sm text-gray-400" style={{ lineHeight: 1.5 }}>
            © {year}{' '}
            <span className="font-semibold text-gray-300">Wincrest Logistics LLC</span>. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export { FooterSection, FooterNav, SocialLinks, LegalLinks };
